import React from 'react';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import {
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
  FormFeedback,
  Button,
  UncontrolledTooltip,
} from 'reactstrap';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { Icon } from 'js/components';
import JiraBoardSelector from './JiraBoardSelector';
import styles from './JiraConfiguration.module.scss';
import {
  configuredRemovedStatuses,
  formatRemovedStatuses,
  parseRemovedStatuses,
} from 'js/connectors/jira/removedStatuses';

const createValidationSchema = () =>
  Yup.object().shape({
    identification_method: Yup.string()
      .required('Identification method is required')
      .oneOf(['project', 'board', 'jql'], 'Invalid identification method'),

    project_key: Yup.string().when('identification_method', {
      is: 'project',
      then: (schema) =>
        schema
          .required('Project key is required')
          .matches(
            /^[A-Z][A-Z0-9]*$/,
            'Project key must be uppercase letters and numbers only'
          )
          .max(10, 'Project key must be 10 characters or less'),
      otherwise: (schema) => schema.notRequired(),
    }),

    board_id: Yup.string().when('identification_method', {
      is: 'board',
      then: (schema) => schema.required('Board selection is required'),
      otherwise: (schema) => schema.notRequired(),
    }),

    jql_query: Yup.string().when('identification_method', {
      is: 'jql',
      then: (schema) => schema.required('JQL query is required'),
      otherwise: (schema) => schema.notRequired(),
    }),

    default_estimate_field: Yup.string().oneOf(
      ['tasks', 'story_points', 'hours'],
      'Invalid default estimate field'
    ),
  });

const JiraConfigurationForm = ({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  isSubmitting,
  isValid,
  onCancel,
  connector,
  existingConfiguration,
  onDelete = () => {},
  isDeleting = false,
}) => {
  const { workspaceId } = useParams();
  // const [isTesting, setIsTesting] = useState(false);
  // const [testResult, setTestResult] = useState(null);

  const formikAttributes = (fieldName) => ({
    id: fieldName,
    name: fieldName,
    value: values[fieldName] || '',
    onChange: handleChange,
    onBlur: handleBlur,
    invalid: !!(errors[fieldName] && touched[fieldName]),
  });

  const feedback = (fieldName) => {
    if (errors[fieldName] && touched[fieldName]) {
      return <FormFeedback>{errors[fieldName]}</FormFeedback>;
    }
    return null;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(e);
  };

  const handleSelectedBoardChange = (selectedBoard) => {
    if (!selectedBoard) {
      setFieldValue('board_project_key', '');
      setFieldValue('board_project_name', '');
      setFieldValue('board_name', '');
      return;
    }

    setFieldValue(
      'board_project_key',
      selectedBoard.location?.projectKey || ''
    );
    setFieldValue(
      'board_project_name',
      selectedBoard.location?.projectName || ''
    );
    setFieldValue('board_name', selectedBoard.name || '');
  };

  // const handleTestConnection = async () => {
  //   setIsTesting(true);
  //   setTestResult(null);

  //   try {
  //     // TODO: Implement test connection for team configuration
  //     setTestResult({
  //       success: true,
  //       message: 'Connection test successful!',
  //     });
  //   } catch (error) {
  //     setTestResult({
  //       success: false,
  //       message: 'Connection test failed. Please check your configuration.',
  //     });
  //   } finally {
  //     setIsTesting(false);
  //   }
  // };

  return (
    <Form onSubmit={handleFormSubmit}>
      <h6>Jira Configuration for {connector.name}</h6>

      <FormGroup>
        <Label for="identification_method">
          How do you identify this team's data in Jira?
        </Label>
        <Input
          type="select"
          {...formikAttributes('identification_method')}
          className="limited-width-lg"
          required
        >
          <option value="">Select identification method</option>
          <option value="project">By project key</option>
          <option value="board">By board</option>
          <option value="jql">By JQL query</option>
        </Input>
        {feedback('identification_method')}
      </FormGroup>

      {values.identification_method === 'project' && (
        <FormGroup>
          <Label for="project_key">Jira project key</Label>
          <Input
            type="text"
            {...formikAttributes('project_key')}
            placeholder="PROJ"
            className="limited-width-lg"
            required
          />
          {feedback('project_key')}
          <FormText color="muted">
            The Jira project key (e.g., PROJ, DEV, BUG)
          </FormText>
        </FormGroup>
      )}

      {values.identification_method === 'board' && (
        <FormGroup>
          <Label for="board_id">Jira Board</Label>
          <JiraBoardSelector
            value={values.board_id}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.board_id}
            touched={touched.board_id}
            workspaceId={workspaceId}
            connectorId={connector.id}
            persistedProjectKey={values.board_project_key}
            persistedProjectName={values.board_project_name}
            persistedBoardName={values.board_name}
            onSelectedBoardChange={handleSelectedBoardChange}
          />
        </FormGroup>
      )}

      {values.identification_method === 'jql' && (
        <FormGroup>
          <Label for="jql_query">JQL Query</Label>
          <Input
            type="textarea"
            {...formikAttributes('jql_query')}
            placeholder="project = PROJ AND assignee in (teamMembers)"
            rows={3}
            required
          />
          {feedback('jql_query')}
          <FormText color="muted">
            JQL query to identify the team's issues (e.g., project = PROJ AND
            assignee in (teamMembers))
          </FormText>
        </FormGroup>
      )}

      <FormGroup>
        <Label for="default_estimate_field">Default estimate field</Label>
        <Input
          type="select"
          {...formikAttributes('default_estimate_field')}
          className="limited-width-lg"
        >
          <option value="tasks">Work item count</option>
          <option value="story_points">Story points</option>
          <option value="hours">Time</option>
        </Input>
        {feedback('default_estimate_field')}
      </FormGroup>

      <FormGroup>
        <Label for="removed_statuses">
          Removed statuses
          <span
            id="removed-statuses-info"
            className="text-muted ml-1"
            tabIndex={0}
            aria-label="About removed statuses"
          >
            <Icon icon={faCircleInfo} />
          </span>
        </Label>
        <UncontrolledTooltip
          placement="right"
          target="removed-statuses-info"
          innerClassName={styles['info-tooltip']}
        >
          Comma-separated list of Jira statuses that mean work was dropped
          rather than finished. Moving into one counts as removing the item
          rather than completing it.
        </UncontrolledTooltip>
        <Input
          type="text"
          {...formikAttributes('removed_statuses')}
          className="limited-width-lg"
        />
      </FormGroup>

      {/* Test Connection Section */}
      {/* <div className="border-top pt-3">
        <h6>Test Configuration</h6>
        <p className="text-muted small mb-3">
          Test your Jira configuration to verify the project key and settings
          are correct.
        </p>

        {testResult && (
          <div
            className={`alert ${
              testResult.success ? 'alert-success' : 'alert-danger'
            } mb-3`}
          >
            {testResult.message}
          </div>
        )}

        <Button
          type="button"
          color="info"
          onClick={handleTestConnection}
          disabled={isTesting || !values.identification_method}
        >
          {isTesting ? 'Testing...' : 'Test Configuration'}
        </Button>
      </div> */}

      {/* Action buttons */}
      <Button color="primary" type="submit" disabled={isSubmitting || !isValid}>
        {isSubmitting
          ? 'Saving...'
          : existingConfiguration
          ? 'Update Configuration'
          : 'Save Configuration'}
      </Button>
      <Button color="secondary" onClick={onCancel} disabled={isSubmitting}>
        Cancel
      </Button>
      <Button color="danger" onClick={onDelete} disabled={isDeleting}>
        Delete
      </Button>
    </Form>
  );
};

const JiraConfiguration = withFormik({
  mapPropsToValues: ({ existingConfiguration = {} }) => ({
    identification_method: existingConfiguration?.identification_method || '',
    project_key: existingConfiguration?.project_key || '',
    board_id: existingConfiguration?.board_id || '',
    board_project_key: existingConfiguration?.board_project_key || '',
    board_project_name: existingConfiguration?.board_project_name || '',
    board_name: existingConfiguration?.board_name || '',
    jql_query: existingConfiguration?.jql_query || '',
    default_estimate_field:
      existingConfiguration?.default_estimate_field || 'tasks',
    removed_statuses: formatRemovedStatuses(
      configuredRemovedStatuses(existingConfiguration)
    ),
  }),

  validationSchema: createValidationSchema(),

  handleSubmit: (values, { props, setSubmitting }) => {
    if (props.onSubmit) {
      props.onSubmit({
        ...values,
        removed_statuses: parseRemovedStatuses(values.removed_statuses),
      });
    }
    setSubmitting(false);
  },

  enableReinitialize: true,
})(JiraConfigurationForm);

export default JiraConfiguration;
